import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { buttonClass } from '@/components/ui/Button';
import { useI18n } from '@/hooks/useI18n';
import { precioDe, type Plan } from './content';
import styles from './PricingSection.module.css';

function PlanCard({ plan }: { plan: Plan }) {
  const { t } = useI18n();
  const p = t.marketing.pricing;

  // El estado guarda el índice, pero la variante se identifica por `id`: el
  // orden es el mismo en todos los diccionarios y el `id` no se traduce, así
  // que cambiar de idioma no cambia de variante ni de precio.
  const [variantIndex, setVariantIndex] = useState(0);
  const variant = plan.variants[variantIndex];
  const hasVariants = plan.variants.length > 1;

  const precio = precioDe(plan.id, variant.id) ?? null;

  return (
    <article className={[styles.plan, plan.featured && styles.featured].filter(Boolean).join(' ')}>
      {plan.featured && <span className={styles.featuredTag}>{p.featuredTag}</span>}
      <h3 className={styles.name}>{plan.name}</h3>

      {hasVariants && (
        <div className={styles.variantTabs}>
          {plan.variants.map((option, index) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={index === variantIndex}
              onClick={() => setVariantIndex(index)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      <div className={styles.priceRow}>
        {/* El precio sale de `content.ts` (una sola fuente para todos los
            idiomas) y el diccionario solo decide cómo se presenta: en inglés
            hay que decir la moneda, porque "$2,999.00" a secas se lee como
            dólares y son pesos. */}
        <span className={styles.price}>{precio ? p.formatoPrecio(precio) : p.free}</span>
        {variant.priceNote && precio && <span className={styles.priceNote}>{variant.priceNote}</span>}
        {!precio && <span className={styles.priceNote}>{p.freeNote}</span>}
      </div>

      {variant.description && <p className={styles.description}>{variant.description}</p>}

      <ul className={styles.features}>
        {variant.features.map((feature, i) => (
          <li key={i} className={styles.feature}>
            <Check size={14} aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className={styles.ctas}>
        {/*
          Al formulario viajan los identificadores además de las etiquetas: lo
          que se guarda en el lead tiene que ser el mismo dato venga de donde
          venga, o ventas termina con "Smart Match Subscription — Brands" en
          unas filas y "Suscripción Smart Match — Marcas" en otras, y cualquier
          filtro sobre ese texto deja de funcionar.
        */}
        <Link
          to="/contacto"
          state={{
            planId: plan.id,
            variantId: variant.id,
            plan: plan.name,
            variant: variant.label,
          }}
          className={buttonClass({ variant: plan.featured ? 'primary' : 'outline', block: true })}
        >
          {plan.ctaLabel}
        </Link>
        {plan.secondaryCtaLabel && (
          <Link
            to="/contacto"
            state={{
              planId: plan.id,
              variantId: variant.id,
              plan: plan.name,
              variant: variant.label,
              tipo: 'demo',
            }}
            className={buttonClass({ variant: 'ghost', size: 'sm', block: true })}
          >
            {plan.secondaryCtaLabel}
          </Link>
        )}
      </div>
    </article>
  );
}

export function PricingSection() {
  const { t } = useI18n();
  const p = t.marketing.pricing;

  return (
    <section className="ilu-section" id="planes">
      <div className="ilu-container">
        <span className="ilu-eyebrow">{p.eyebrow}</span>
        <h2 className="ilu-section-title">{p.title}</h2>
        <p className="ilu-lead">{p.lead}</p>

        <div className={styles.grid}>
          {t.marketing.plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>

        <p className={styles.customNote}>
          {p.customNote} <Link to="/contacto">{p.customCta}</Link>
        </p>
      </div>
    </section>
  );
}
